<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { ElMessage } from 'element-plus';
import { MessageCircle } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';

import { useChatwoot } from '@/composables/useChatwoot';

const { t } = useI18n();
const { toggleChatwoot } = useChatwoot();
const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const status = ref('loading');
let loadingTimeout;

const label = computed(() => {
  if (status.value === 'loading') return t('Support chat is loading');
  if (status.value === 'unavailable') return t('Support chat is unavailable');
  return t('Support');
});

const onReady = () => {
  status.value = 'ready';
  window.clearTimeout(loadingTimeout);
};

const onError = () => {
  status.value = 'unavailable';
  window.clearTimeout(loadingTimeout);
};

const openChat = () => {
  // The header can remount after the ready event, or the SDK can load after the timeout.
  if (window.$chatwoot?.hasLoaded) {
    onReady();
    if (toggleChatwoot()) return;
    onError();
  }

  if (status.value === 'loading') {
    ElMessage.info(t('Support chat is still loading. Please try again shortly.'));
  } else {
    ElMessage.warning(t('Support chat is unavailable. Please try again later.'));
  }
};

onMounted(() => {
  window.addEventListener('chatwoot:ready', onReady);
  window.addEventListener('chatwoot:error', onError);

  if (window.$chatwoot?.hasLoaded) {
    onReady();
  } else if (isLocal || window.chatwootLoadFailed) {
    onError();
  } else {
    loadingTimeout = window.setTimeout(onError, 15000);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('chatwoot:ready', onReady);
  window.removeEventListener('chatwoot:error', onError);
  window.clearTimeout(loadingTimeout);
});
</script>

<template>
  <button
    class="support-chat"
    type="button"
    :title="label"
    :aria-label="label"
    :aria-busy="status === 'loading'"
    @click="openChat"
  >
    <MessageCircle
      :size="20"
      :stroke-width="1.5"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped>
.support-chat {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--wp-surface-bg-icon);
  color: var(--wp-text-primary);
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.support-chat:hover {
  background: var(--wp-surface-input);
}

.support-chat:focus-visible {
  outline: 2px solid var(--wp-state-main);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .support-chat {
    width: 44px;
    height: 44px;
  }
}
</style>
